import * as Sentry from "@sentry/react";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { keepPreviousData, MutationCache, QueryClient } from "@tanstack/react-query";

import { errors, FORBIDDEN_RESOURCE } from "@/features/api/const/errors";
import { handleApiError } from "@/features/api/utils/handleApiError";

import { FIVE_MINUTES } from "./features/query/const";

const FORBIDDEN_STATUS = 403;
const CLIENT_ERROR_STATUS = { from: 400, to: 500 };
const MAX_QUERY_RETRIES = 1;

const getErrorMessage = (error: unknown): string | undefined =>
  typeof error === "object" && error !== null && "message" in error && typeof error.message === "string"
    ? error.message
    : undefined;

const isHandledApiError = (error: unknown): boolean => {
  const message = getErrorMessage(error);

  return message !== undefined && Object.hasOwn(errors, message);
};

const getStatusCode = (error: unknown): number | undefined =>
  typeof error === "object" && error !== null && "statusCode" in error && typeof error.statusCode === "number"
    ? error.statusCode
    : undefined;

// Le code prime sur le message : l'API renvoie une douzaine de messages techniques distincts
// avec un 403, et `requester` relaie le message sans le code.
const isForbidden = (error: unknown): boolean =>
  getStatusCode(error) === FORBIDDEN_STATUS || getErrorMessage(error) === FORBIDDEN_RESOURCE;

// Un 4xx est déterministe : le rejouer redonne le même rejet. Le `statusCode` n'est présent que
// sur les erreurs du client généré ; `requester` relaie le message sans le code, d'où les deux
// reconnaissances par message qui suivent.
const isClientError = (error: unknown): boolean => {
  const status = getStatusCode(error);

  if (status !== undefined) {
    return status >= CLIENT_ERROR_STATUS.from && status < CLIENT_ERROR_STATUS.to;
  }

  return isForbidden(error) || isHandledApiError(error);
};

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      // Un 403 sur une action lancee par l'utilisateur veut dire que l'UI a propose une action
      // qu'elle aurait du masquer : on previent l'utilisateur avec un message generique — les
      // messages techniques de l'API ne lui parlent pas — ET on garde le signal dans Sentry.
      if (isForbidden(error)) {
        handleApiError(FORBIDDEN_RESOURCE, {});
        Sentry.captureException(error, {
          extra: { cause: error instanceof Error ? error.cause : undefined },
        });
        return;
      }

      // Les autres 4xx sont le serveur qui refuse ce que l'utilisateur a demandé — propriété unique
      // en double, inventaire disparu sous ses pieds — pas un défaut du front. Ne filtrer que les
      // messages connus laissait passer tout rejet porté par un objet brut
      // `{ error, message, statusCode }`, forme que l'API renvoie sans passer par `requester`.
      if (isClientError(error)) {
        return;
      }

      Sentry.captureException(error, {
        extra: { cause: error instanceof Error ? error.cause : undefined },
      });
    },
  }),
  defaultOptions: {
    queries: {
      gcTime: FIVE_MINUTES, // 5 min
      placeholderData: keepPreviousData,
      retry: (failureCount, error) => !isClientError(error) && failureCount < MAX_QUERY_RETRIES,
      staleTime: 120_000,
    },
  },
});

export const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});
